import { EventBarrier } from "@/components/common/event-barrier";
import { AppAwareHtml } from "@/components/r3f/renderers/app-aware-html";
import { ANNOTATION_ZINDEX_RANGE_MAP } from "@/components/ui/annotations/annotation-zindex-range-map";
import { useNonExhaustiveEffect } from "@faro-lotv/app-component-toolbox";
import { blue, FaroText, grey, TextField } from "@faro-lotv/flat-ui";
import { MAX_NAME_LENGTH } from "@faro-lotv/ielement-types";
import { MutableRefObject, useState, type JSX } from "react";
import { Vector3 } from "three";

type WaypointNameEditorProps<T> = {
  /**
   * A generic reference to the waypoint being edited.
   * Should be stable like an id, but can contain more information that should be passed along to the callback.
   * The input is re-initialized when this value changes.
   */
  waypointInfo: T;

  /** The initial name to show in the editor */
  initialName: string;

  /** The maximum length of the name. @default MAX_NAME_LENGTH for iElements */
  maxLength?: number;

  /** The waypoint position in three.js world space. */
  anchorPoint: Vector3;

  /** The parent that the editor should have in the html DOM */
  parentRef: MutableRefObject<HTMLElement>;

  /** Callback when the user inputs a name */
  onApplyName(id: T, newName: string): void;

  /** The callback function to exit the name editor */
  onExit(): void;
};

/** Font size of the text in the label */
const LABEL_FONT_SIZE = "0.75rem";

/**
 * @returns name editor shown for waypoints. Applies changes on blur and when pressing Enter.
 */
export function WaypointNameEditor<T>({
  parentRef,
  anchorPoint,
  maxLength = MAX_NAME_LENGTH,
  waypointInfo,
  initialName,
  onApplyName,
  onExit,
}: WaypointNameEditorProps<T>): JSX.Element {
  const [text, setText] = useState(initialName);
  const [textRef, setTextRef] = useState<HTMLInputElement>();
  const [prevWaypointInfo, setPrevWaypointInfo] = useState<T>();

  // Make sure that when the ID updates reactively, that we initialize the component again.
  useNonExhaustiveEffect(() => {
    if (!textRef) return;

    if (prevWaypointInfo && textRef.value !== initialName) {
      onApplyName(prevWaypointInfo, textRef.value);
    }

    textRef.value = initialName;
    textRef.focus();
    textRef.select();

    setPrevWaypointInfo(waypointInfo);
  }, [textRef, waypointInfo]);

  return (
    <AppAwareHtml
      portal={parentRef}
      position={anchorPoint}
      zIndexRange={ANNOTATION_ZINDEX_RANGE_MAP.toolbar}
      eps={-1}
      style={{
        pointerEvents: "auto",
        // Add offset to put the container on top of location marker label
        transform: "translate(-50% , 9px)",
      }}
    >
      <EventBarrier>
        <TextField
          inputProps={{
            "aria-label": "waypoint name editor",
            maxLength,
          }}
          // The component has limited space. We only want to show the text length counter when reaching the max length.
          endAdornment={
            text.length >= maxLength ? (
              <FaroText
                variant="placeholder"
                sx={{ fontSize: LABEL_FONT_SIZE, marginRight: -1 }}
              >{`${text.length}/${maxLength}`}</FaroText>
            ) : undefined
          }
          placeholder={initialName}
          inputRef={setTextRef}
          autoFocus
          controlStyle={{ width: 105 }}
          sx={{
            height: "fit-content",
            input: { fontSize: LABEL_FONT_SIZE },
            background: blue[800],
            color: grey[0],
            outline: `1px solid ${grey[0]}33`,
            ".MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            ".MuiInputBase-input": {
              padding: 0.75,
            },
            ".MuiInputBase-input::placeholder": {
              color: grey[500],
              opacity: 1,
            },
          }}
          onFocus={(e) => {
            e.target.select();
            e.target.value = initialName;
          }}
          onKeyDown={(ev) => {
            if (ev.key === "Enter") {
              textRef?.blur();
            }
            if (ev.key === "Escape") {
              ev.stopPropagation();
              ev.currentTarget.value = initialName;
              onExit();
            }
            if (ev.key === "Tab") {
              textRef?.blur();
            }
          }}
          onTextChanged={setText}
          onBlur={(ev) => {
            onApplyName(
              waypointInfo,
              ev.currentTarget.value.trim() || initialName,
            );
            onExit();
          }}
        />
      </EventBarrier>
    </AppAwareHtml>
  );
}
